import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row, Button } from 'react-bootstrap';
import {
  DecimalFormat,
  IntegerFormat,
  MoneyFormat
} from '../Common/TextFormat';
import {
  CustomerData,
  TermDetail,
  Terms,
  LineOfCreditOffer
} from 'states/initialState';
import { useAppData } from 'contexts/appContext';
import { useTracking } from 'react-tracking';

interface MerchantDetailProps {
  businessId: string;
  offer: TermDetail;
  customerData: CustomerData;
  lineOfCreditOffer: LineOfCreditOffer | null;
}

const MerchantDetails = ({
  businessId,
  offer,
  customerData,
  lineOfCreditOffer
}: MerchantDetailProps) => {
  const intl = useIntl();
  const [viewApprovedOffers, setApprovedOffersVisibility] = useState(false);
  const { partnerDetails } = useAppData();
  const { trackEvent } = useTracking();

  const renderToggleButton = (breakpointSuffix: string) => {
    return (
      <Button
        variant="link"
        className="text-decoration-none fs-4 fw-500 pb-0 ps-1 d-flex lh-lg align-items-center"
        onClick={toggleAllApprovedVisibility}
        data-testid={`toggleAllApprovedVisibility${breakpointSuffix}`}
      >
        <span
          data-testid={`toggleAllApprovedVisibilityText${breakpointSuffix}`}
        >
          {viewApprovedOffers ? (
            <FormattedMessage id="merchantDetail.hideAllApprovedOffers" />
          ) : (
            <FormattedMessage id="merchantDetail.viewAllApprovedOffers" />
          )}
        </span>
        <i
          className={
            'icon ms-2 ' + (viewApprovedOffers ? 'chevron-up' : 'chevron-down')
          }
        ></i>
      </Button>
    );
  };

  const toggleAllApprovedVisibility = () => {
    setApprovedOffersVisibility(!viewApprovedOffers);
    if (!viewApprovedOffers) {
      trackEvent({
        event: 'View All Approved Offers',
        userProfileName: partnerDetails.partner,
        opportunityId: businessId,
        termLoanMaxApproval: offer?.maxLineAmount,
        locMaxApproval: lineOfCreditOffer?.lineAmount
      });
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          <Row>
            <div className="col-sm-8">
              <h2>{customerData.companyName}</h2>
              <Row className="fs-5 g-2">
                <div className="col-sm lh-lg d-flex">
                  <span className="me-2">
                    <FormattedMessage id="merchantDetail.dba" />
                  </span>
                  <span className="fw-medium text-break">
                    {customerData.doingBusinessAs}
                  </span>
                </div>
                <div className="col-sm lh-lg d-flex">
                  <span className="me-2">
                    <FormattedMessage id="merchantDetail.industry" />
                  </span>
                  <span className="fw-medium text-break">
                    {customerData.industry}
                  </span>
                </div>
              </Row>
              <Row className="fs-5 g-2">
                <div className="col-sm lh-lg">
                  <span className="me-2">
                    <FormattedMessage id="merchantDetail.selfReportedAnnualRevenue" />
                  </span>
                  <span className="fw-medium text-break">
                    <MoneyFormat
                      value={customerData.selfReportedAnnualRevenue}
                      variant="text"
                    />
                  </span>
                </div>
                <div className="col-sm lh-lg">
                  <span className="me-2">
                    <FormattedMessage id="merchantDetail.estimatedAnnualRevenue" />
                  </span>
                  <span className="fw-medium text-break">
                    <MoneyFormat
                      value={customerData.estimatedAnnualRevenue}
                      variant="text"
                    />
                  </span>
                </div>
              </Row>
              <Row className="fs-5 g-2">
                <div className="col-sm lh-lg d-flex">
                  <span className="me-2">Email</span>
                  <span className="fw-medium text-break">
                    <a href={'mailto:' + customerData.email}>
                      {customerData.email}
                    </a>
                  </span>
                </div>
              </Row>
              <Row className="fs-5 g-2 d-none d-sm-block">
                {renderToggleButton('SM')}
              </Row>
            </div>
            <div className="col-sm-4 mt-3 mt-sm-0">
              <div className="p-3 bg-light">
                <div className="fw-normal text-center">
                  <FormattedMessage id="merchantDetail.termLoanUpTo" />
                </div>
                <h4 className="fw-bold text-secondary text-center">
                  <MoneyFormat value={offer?.maxLineAmount} cents={false} />
                </h4>
                <hr className="bg-line mt-2 mb-2" />
                {lineOfCreditOffer ? (
                  <>
                    <div className="fw-normal text-center">
                      <FormattedMessage id="merchantDetail.lineOfCreditUpTo" />
                    </div>
                    <h4 className="fw-bold text-secondary text-center">
                      <MoneyFormat
                        value={lineOfCreditOffer.lineAmount}
                        cents={false}
                      />
                    </h4>
                  </>
                ) : (
                  <div className="fw-normal text-center">
                    <FormattedMessage id="merchantDetail.lineOfCreditNotApproved" />
                  </div>
                )}
              </div>
            </div>
          </Row>
          <div className="mt-2 mb-2 d-block d-sm-none">
            {renderToggleButton('XS')}
          </div>
          {viewApprovedOffers && (
            <div className="table-responsive mt-2 mt-sm-3">
              <table className="table" data-testid="allApprovalsTable">
                <thead className="bg-secondary text-white fs-4">
                  <tr>
                    <th style={{ width: '42%' }}>
                      <FormattedMessage id="merchantDetail.product" />
                    </th>
                    <th className="text-end" style={{ width: '14%' }}>
                      <FormattedMessage id="merchantDetail.term" />
                    </th>
                    <th className="text-end" style={{ width: '17%' }}>
                      <FormattedMessage id="merchantDetail.approvalRange" />
                    </th>
                    <th className="text-end" style={{ width: '12%' }}>
                      <FormattedMessage id="merchantDetail.buyRate" />
                    </th>
                    <th className="text-end" style={{ width: '15%' }}>
                      <FormattedMessage id="merchantDetail.maxSellRate" />
                    </th>
                  </tr>
                </thead>
                {offer && (
                  <tbody className="border-bottom-2 fs-5">
                    {offer.terms.map((term: Terms, idx: any) => {
                      return (
                        <tr key={idx}>
                          <td>
                            {idx === 0 && (
                              <span>
                                <FormattedMessage id="merchantDetail.termLoan" />
                              </span>
                            )}
                          </td>
                          <td className="text-end">
                            <IntegerFormat value={term.term} variant="text" />
                          </td>
                          <td className="text-end">
                            <MoneyFormat
                              value={term.lineAmount}
                              variant="text"
                              cents={false}
                            />
                          </td>
                          <td className="text-end">
                            <MoneyFormat
                              value={term.buyRate}
                              decimalScale={3}
                              toFixed={false}
                              variant="text"
                            />
                          </td>
                          <td className="text-end">
                            <MoneyFormat
                              value={term.sellRate}
                              decimalScale={3}
                              toFixed={false}
                              variant="text"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
                {lineOfCreditOffer && (
                  <tfoot className="border-bottom border-light fs-5">
                    <tr>
                      <td>
                        <FormattedMessage id="merchantDetail.lineOfCredit" />
                      </td>
                      <td className="text-end">
                        <IntegerFormat
                          value={lineOfCreditOffer?.term}
                          variant="text"
                        />
                      </td>
                      <td className="text-end">
                        <MoneyFormat
                          value={lineOfCreditOffer?.lineAmount}
                          variant="text"
                          cents={false}
                        />
                      </td>
                      <td className="text-end">
                        <DecimalFormat
                          value={lineOfCreditOffer?.apr}
                          suffix="% APR"
                          variant="text"
                        />
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default MerchantDetails;
